import OptimizedImage from "@/components/OptimizedImage";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Quote, User } from "lucide-react";

interface ExpertCardProps {
  image: string;
  name: string;
  title: string;
  quote: string;
  imageScale?: number;
  bio?: string;
  credentials?: string[];
  roleType?: "tech" | "care" | "ethics";
  variant?: "compact" | "full";
}

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  tech: { label: "Technologie & KI", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  care: { label: "Pflege & Praxis", color: "bg-green-500/10 text-green-600 border-green-500/20" },
  ethics: { label: "Ethik & Recht", color: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
};

const ExpertCard = ({ 
  image, 
  name, 
  title, 
  quote, 
  imageScale = 1,
  bio,
  credentials,
  roleType,
  variant = "compact"
}: ExpertCardProps) => {
  // Compact variant (original behavior)
  if (variant === "compact") {
    return (
      <div className="text-center">
        <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden shadow-md">
          <OptimizedImage
            className="w-full h-full object-cover" 
            style={{ transform: `scale(${imageScale})` }}
            src={image}
            alt={`Portrait ${name}`}
            priority={false}
          />
        </div>
        <h3 className="text-foreground mb-2">{name}</h3>
        <p className="text-primary font-medium mb-4">{title}</p>
        <p className="text-muted-foreground italic">"{quote}"</p>
      </div>
    );
  }

  // Full Enterprise variant
  return (
    <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5">
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row gap-6 p-6">
          {/* Image Section */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-xl overflow-hidden shadow-lg ring-2 ring-border/50 mx-auto md:mx-0">
              {image ? (
                <OptimizedImage
                  className="w-full h-full object-cover" 
                  style={{ transform: `scale(${imageScale})` }}
                  src={image}
                  alt={`Portrait ${name}`}
                  priority={false}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  <User className="w-16 h-16 text-primary/40" />
                </div>
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-grow space-y-4">
            {/* Header with Name, Title, Role Badge */}
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h3 className="text-xl md:text-2xl font-bold text-foreground">
                  {name}
                </h3>
                {roleType && ROLE_LABELS[roleType] && (
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${ROLE_LABELS[roleType].color}`}
                  >
                    {ROLE_LABELS[roleType].label}
                  </Badge>
                )}
              </div>
              <p className="text-primary font-semibold">
                {title}
              </p>
            </div>

            {/* Quote */}
            <blockquote className="relative pl-4 border-l-2 border-primary/50">
              <Quote className="absolute -left-3 -top-1 h-5 w-5 text-primary/30 bg-card" />
              <p className="text-muted-foreground italic text-sm md:text-base">
                "{quote}"
              </p>
            </blockquote>

            {/* Bio */}
            {bio && (
              <p className="text-muted-foreground text-sm leading-relaxed">
                {bio}
              </p>
            )}

            {/* Credentials Section */}
            {credentials && credentials.length > 0 && (
              <div className="pt-3 border-t border-border/50">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  Verifizierte Kompetenzen
                </h4>
                <div className="flex flex-wrap gap-2">
                  {credentials.map((credential, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary"
                      className="text-xs py-1 px-2 bg-primary/5 hover:bg-primary/10 border border-primary/10"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1 text-primary" />
                      {credential}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExpertCard;
